/**
 * Players must never show a broken or empty profile picture. Instead of touching
 * every template, one capture-phase `error` listener on the document swaps any
 * failed profile-picture <img> (uploads, DiceBear, missing src) to the static
 * default avatar that ships with every build (same file the backend assigns).
 */
export const DEFAULT_AVATAR_URL = '/assets/images/icons/avatar-placeholder.svg';

/** Opt-in marker for avatar images whose URL does not match the patterns below. */
export const AVATAR_FALLBACK_ATTRIBUTE = 'data-avatar-fallback';

const AVATAR_SRC = /\/uploads\/|dicebear/i;
const MISSING_SRC = /^(|null|undefined)$|\/(null|undefined)$/;

export function isDefaultAvatar(src: string | null | undefined): boolean {
  return !!src && src.split('?')[0].endsWith(DEFAULT_AVATAR_URL);
}

/** True for <img> elements that render a profile picture and failed to load it. */
export function shouldFallBackToDefaultAvatar(img: HTMLImageElement): boolean {
  const src = (img.getAttribute('src') ?? '').trim();
  if (isDefaultAvatar(src)) return false; // never loop if the default itself fails
  if (img.hasAttribute(AVATAR_FALLBACK_ATTRIBUTE)) return true;
  return AVATAR_SRC.test(src) || MISSING_SRC.test(src);
}

/** Installs the document-wide fallback; returns an uninstall function. */
export function installAvatarFallback(doc: Document): () => void {
  const onError = (event: Event) => {
    const img = event.target;
    if (!(img instanceof HTMLImageElement) || !shouldFallBackToDefaultAvatar(img)) return;
    img.removeAttribute('srcset');
    img.src = DEFAULT_AVATAR_URL;
  };
  // `error` does not bubble, so listen in the capture phase.
  doc.addEventListener('error', onError, true);
  return () => doc.removeEventListener('error', onError, true);
}
